import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dropdown, Tooltip } from 'antd';
import {
  CirclePlus,
  CircleUser,
  Headphones,
  LayoutDashboard,
  Megaphone,
  MicVocal,
  Settings,
  Wallet,
} from 'lucide-react';
import { ThreeDotsIcon, SidebarCollapseIcon, SidebarExpandIcon, LogoutIcon } from '../../../../Assets/Icons';
import { Auth0Logout, imageLink } from '../../../../Constant/Utils';
import { ADMIN_ROLES, canManagePayouts } from '../../../../utils/adminRoles';

import logo from './../../../../Assets/Images/songplace.png';
import logoIcon from './../../../../Assets/Images/logo.png';
import './sidebar.scss';

const AdminSidebar = ({ onClose, collapsed, onToggle }) => {
  const navigate = useNavigate();
  const auth0User = useSelector(e => e.authenticationSlice.auth0User);
  const user = useSelector(e => e.authenticationSlice.user);
  const adminRole = useSelector(e => e.authenticationSlice.adminRole);
  const [pathname, setPathname] = useState('');

  const items = [
    {
      label: (
        <div
          onClick={() => {
            Auth0Logout();
          }}
        >
          Logout
        </div>
      ),
      key: '0',
    },
  ];

  useEffect(() => {
    let pathName = window.location.pathname;
    setPathname(pathName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  return (
    <div className={`adminSidebarNew ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebarHeader">
        <div className="logoWrapper">
          <img src={collapsed ? logoIcon : logo} alt="logo" className={collapsed ? 'logoIcon' : ''} />
        </div>
        {!onClose && (
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <button className="collapseToggleBtn" onClick={onToggle}>
              {collapsed ? <SidebarExpandIcon /> : <SidebarCollapseIcon />}
            </button>
          </Tooltip>
        )}
      </div>

      <div className={`userProfileCard ${collapsed ? 'collapsed' : ''}`}>
        <div className="userAvatar">
          <img src={imageLink(user?.image || auth0User?.picture)} alt="user" />
          <span className="statusDot"></span>
        </div>
        {!collapsed ? (
          <>
            <div className="userInfo">
              <p className="userName">{user?.name || 'Admin User'}</p>
              <span className="userRole">
                {adminRole === ADMIN_ROLES.SUPER_ADMIN ? 'Super Admin' : 'Admin Manager'}
              </span>
            </div>
            <Dropdown
              overlayClassName="dropdownSideBar"
              menu={{
                items,
              }}
              trigger={['click']}
            >
              <button className="menuBtn">
                <ThreeDotsIcon />
              </button>
            </Dropdown>
          </>
        ) : (
          <Dropdown
            overlayClassName="dropdownSideBar"
            menu={{
              items,
            }}
            trigger={['click']}
          >
            <button className="menuBtn collapsedMenuBtn" title="Logout">
              <LogoutIcon color="#ffffff" />
            </button>
          </Dropdown>
        )}
      </div>

      <nav className="sidebarNav">
        <div className="navSection">
          {!collapsed && <span className="sectionLabel">Main Menu</span>}
          <Tooltip title={collapsed ? 'Dashboard' : ''} placement="right">
            <div
              className={pathname === '/dashboard' ? 'navItem active' : 'navItem'}
              onClick={() => {
                navigate('/dashboard');
                if (onClose) onClose();
              }}
            >
              <div className="navIconWrapper">
                <LayoutDashboard />
              </div>
              <span className="navText">Dashboard</span>
            </div>
          </Tooltip>
          <Tooltip title={collapsed ? 'Campaigns' : ''} placement="right">
            <div
              className={
                pathname === '/dashboard/campaigns'
                || pathname.includes('/dashboard/campaigns')
                  ? 'navItem active'
                  : 'navItem'
              }
              onClick={() => {
                navigate('/dashboard/campaigns');
                if (onClose) onClose();
              }}
            >
              <div className="navIconWrapper">
                <Megaphone />
              </div>
              <span className="navText">Campaigns</span>
            </div>
          </Tooltip>
          <Tooltip title={collapsed ? 'Curators' : ''} placement="right">
            <div
              className={pathname === '/dashboard/curators' ? 'navItem active' : 'navItem'}
              onClick={() => {
                navigate('/dashboard/curators');
                if (onClose) onClose();
              }}
            >
              <div className="navIconWrapper">
                <Headphones />
              </div>
              <span className="navText">Curators</span>
            </div>
          </Tooltip>
          <Tooltip title={collapsed ? 'Artists' : ''} placement="right">
            <div
              className={pathname === '/dashboard/artists' ? 'navItem active' : 'navItem'}
              onClick={() => {
                navigate('/dashboard/artists');
                if (onClose) onClose();
              }}
            >
              <div className="navIconWrapper">
                <MicVocal />
              </div>
              <span className="navText">Artists</span>
            </div>
          </Tooltip>
          {canManagePayouts(adminRole) && (
            <Tooltip title={collapsed ? 'Payouts' : ''} placement="right">
              <div
                className={pathname === '/dashboard/payouts' ? 'navItem active' : 'navItem'}
                onClick={() => {
                  navigate('/dashboard/payouts');
                  if (onClose) onClose();
                }}
              >
                <div className="navIconWrapper">
                  <Wallet />
                </div>
                <span className="navText">Payouts</span>
              </div>
            </Tooltip>
          )}
          <Tooltip title={collapsed ? 'Create Campaign' : ''} placement="right">
            <div
              className={pathname === '/dashboard/create-campaign' ? 'navItem active' : 'navItem'}
              onClick={() => {
                navigate('/dashboard/create-campaign');
                if (onClose) onClose();
              }}
            >
              <div className="navIconWrapper">
                <CirclePlus />
              </div>
              <span className="navText">Create Campaign</span>
            </div>
          </Tooltip>
        </div>

        <div className="navDivider"></div>

        <div className="navSection">
          {!collapsed && <span className="sectionLabel">Settings</span>}
          <Tooltip title={collapsed ? 'Account overview' : ''} placement="right">
            <div
              className={pathname === '/dashboard/account-overview' ? 'navItem active' : 'navItem'}
              onClick={() => {
                navigate('/dashboard/account-overview');
                if (onClose) onClose();
              }}
            >
              <div className="navIconWrapper">
                <CircleUser />
              </div>
              <span className="navText">Account overview</span>
            </div>
          </Tooltip>
          <Tooltip title={collapsed ? 'Settings' : ''} placement="right">
            <div
              className={pathname === '/dashboard/settings' ? 'navItem active' : 'navItem'}
              onClick={() => {
                navigate('/dashboard/settings');
                if (onClose) onClose();
              }}
            >
              <div className="navIconWrapper">
                <Settings />
              </div>
              <span className="navText">Settings</span>
            </div>
          </Tooltip>
        </div>
      </nav>

      <div className={`sidebarFooter ${collapsed ? 'collapsed' : ''}`}>
        <div className="versionInfo">
          {!collapsed && <span>Songplace Admin</span>}
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
